import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, Plus, Upload, Trash2, Link2, Pencil, X, ChevronRight, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtBytes, fmtDate } from "@/lib/webApi";
import { useMe, PageHead, Empty, primary, ghost } from "@/pages/web/WebApp";
import { FileTable } from "@/pages/web/WebFiles";
import { KpiCard, Section, Card, Pill, EmptyState, ListRow, Eyebrow, Meter } from "@/pages/web/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

export function StorageBar({ storage }) {
  return <Meter label="Storage" used={storage?.used} cap={storage?.cap} data-testid="web-storage-bar" />;
}

export function Overview() {
  const { me } = useMe();
  const [data, setData] = useState({ recent: [], matters: [] });
  const [matters, setMatters] = useState(null);
  const [files, setFiles] = useState(null);
  const [lib, setLib] = useState(null);
  const [billing, setBilling] = useState(null);
  const nav = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  useEffect(() => {
    webApi.get("/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    Promise.all([
      webApi.get("/matters").then((r) => setMatters(r.data)).catch(() => {}),
      webApi.get("/files", { params: { filter: "all" } }).then((r) => setFiles(r.data)).catch(() => {}),
      webApi.get("/files", { params: { filter: "library" } }).then((r) => setLib(r.data)).catch(() => {}),
      webApi.get("/billing").then((r) => setBilling(r.data)).catch(() => {}),
    ]);
  }, []);
  const newMatter = async (e) => {
    e.preventDefault();
    const name = newName;
    setNewOpen(false);
    if (!name) return;
    try {
      const { data: m } = await webApi.post("/matters", { name });
      nav(`/app/matters/${m.id}`);
    } catch (e) { toast.error(errText(e)); }
  };
  const dash = (n) => (n == null ? "–" : n);
  const end = billing?.manual_until || billing?.paid_until;
  const auto = billing?.how === "auto_renew" && !billing?.cancel_requested;
  const endText = end ? `${auto ? "Renews on" : "Ends on"} ${fmtDate(end)}` : "Active";
  const viewAll = "text-sm font-medium text-[--w-accent] hover:underline";
  return (
    <div data-testid="web-overview">
      <PageHead title={me.name ? `Hello, ${me.name.split(" ")[0]}` : "Overview"} sub={new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}>
        <Link to="/app/upload" className={primary} data-testid="overview-upload-button"><Upload className="h-4 w-4" /> Upload</Link>
        <button onClick={() => { setNewName(""); setNewOpen(true); }} className={ghost} data-testid="overview-new-matter-button"><Plus className="h-4 w-4" /> New matter</button>
      </PageHead>
      {!me.store_on && <Card className="mb-4 px-4 py-3 text-sm text-[--w-text-2]" data-testid="store-off-note">Your file store isn't switched on in this environment yet.</Card>}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-8">
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
            <KpiCard eyebrow="Matters" tone="info" value={dash(matters?.length)} sub={`${matters ? matters.filter((m) => m.files > 0).length : "–"} with files`} />
            <KpiCard eyebrow="Files" tone="warn" value={dash(files?.length)} sub={`${dash(lib?.length)} in Library`} />
            <KpiCard eyebrow="Storage" tone="ok" value={fmtBytes(me.storage?.used)} sub={me.storage?.cap ? `of ${fmtBytes(me.storage.cap)}` : "unlimited"} />
            <KpiCard eyebrow="Plan" tone="neutral" value={me.plan_label} sub={endText} />
          </div>
          <Section title="Recent matters" action={<Link to="/app/matters" className={viewAll}>View all →</Link>}>
            {data.matters.length === 0 ? (
              <EmptyState icon={FolderOpen}>
                <p>No matters yet — forward a court document on WhatsApp or upload here.</p>
                <Link to="/app/upload" className={`${ghost} mt-4`}>Upload</Link>
              </EmptyState>
            ) : (
              <Card className="divide-y divide-[--w-border] overflow-hidden">
                {data.matters.map((m) => (
                  <ListRow key={m.id} to={`/app/matters/${m.id}`} data-testid={`overview-matter-${m.id}`}>
                    <span className="font-medium text-[--w-text]">{m.name}</span>
                    <span className="flex items-center gap-2 text-[--w-text-3]">{m.files} file{m.files === 1 ? "" : "s"}<ChevronRight className="h-4 w-4" /></span>
                  </ListRow>
                ))}
              </Card>
            )}
          </Section>
          <Section title="Recent files" action={<Link to="/app/files" className={viewAll}>View all →</Link>}>
            {data.recent.length ? <FileTable rows={data.recent} /> : <Empty>No files yet. Forward one on WhatsApp or upload here.</Empty>}
          </Section>
        </div>
        <div className="space-y-4">
          <Card className="p-5">
            <Eyebrow>Your plan</Eyebrow>
            <p className="mt-1 text-xl font-semibold" data-testid="overview-plan">{me.plan_label}</p>
            <div className="mt-2">
              {billing?.status === "retrying" ? <Pill tone="warn">Payment retrying</Pill>
                : billing?.cancel_requested ? <Pill tone="warn">{end ? `Ends on ${fmtDate(end)}` : "Cancel requested"}</Pill>
                : <Pill tone="ok">{endText}</Pill>}
            </div>
            <Link to="/app/billing" className={`${ghost} mt-4`}>Manage plan</Link>
          </Card>
          <Card className="p-5">
            <Eyebrow className="mb-3">Usage</Eyebrow>
            <StorageBar storage={me.storage} />
          </Card>
        </div>
      </div>
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="theme-warm">
          <DialogHeader><DialogTitle>New matter</DialogTitle></DialogHeader>
          <form onSubmit={newMatter} className="space-y-4">
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Matter name"
              className="w-full rounded-md border border-[--w-border] bg-[--w-card] px-3 py-2 text-sm text-[--w-text] outline-none focus:border-[--w-accent]" />
            <DialogFooter>
              <button type="button" onClick={() => setNewOpen(false)} className={ghost}>Cancel</button>
              <button type="submit" className={primary}>Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Matters() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => webApi.get("/matters", { params: { q } }).then((r) => setRows(r.data)).catch(() => setRows([])), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const k = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); document.getElementById("matter-search")?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div data-testid="web-matters">
      <PageHead title="Matters">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[--w-text-3]" />
          <input id="matter-search" data-testid="matter-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search matters · press /"
            className="w-64 rounded-md border border-[--w-border] bg-[--w-card] py-2 pl-9 pr-3 text-sm text-[--w-text] outline-none focus:border-[--w-accent]" />
        </div>
      </PageHead>
      {rows && rows.length === 0 && <Empty>No matters yet. Forward a court document on WhatsApp and Dicta will suggest one.</Empty>}
      <Card className="divide-y divide-[--w-border]">
        {(rows || []).map((m) => (
          <Link key={m.id} to={`/app/matters/${m.id}`} data-testid={`matter-row-${m.id}`} className="flex items-center justify-between gap-3 px-4 py-3 transition-colors duration-200 hover:bg-[--w-card-2]">
            <div>
              <p className="text-sm font-medium text-[--w-text]">{m.name}</p>
              {m.linked.length > 0 && <p className="text-xs text-[--w-text-3]">Linked: {m.linked.map((l) => `${l.name} (${l.kind})`).join(", ")}</p>}
            </div>
            <Pill className="tabular-nums">{m.files} file{m.files === 1 ? "" : "s"}</Pill>
          </Link>
        ))}
      </Card>
    </div>
  );
}

const selectCls = "rounded-md border border-[--w-border] bg-[--w-card] px-2 py-2 text-sm text-[--w-text]";

function LinkMatter({ id, onDone }) {
  const [all, setAll] = useState([]);
  const [other, setOther] = useState("");
  const [kind, setKind] = useState("connected");
  useEffect(() => {
    webApi.get("/matters").then((r) => setAll(r.data.filter((m) => m.id !== id)));
  }, [id]);
  const save = async () => {
    if (!other) return;
    try { await webApi.post(`/matters/${id}/links`, { other_id: other, kind }); toast.success("Linked"); onDone(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Card className="mb-4 flex flex-wrap gap-2 p-4" data-testid="link-matter-form">
      <select value={other} onChange={(e) => setOther(e.target.value)} data-testid="link-matter-select" className={selectCls}>
        <option value="">Choose a matter</option>
        {all.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="link-kind-select" className={selectCls}>
        {["appeal", "bail", "interim", "connected", "other"].map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <button onClick={save} className={primary} data-testid="link-matter-save">Link</button>
    </Card>
  );
}

export function MatterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [linking, setLinking] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const confirmRef = useRef(null);
  const load = useCallback(() => webApi.get(`/matters/${id}`).then((r) => setM(r.data)).catch(() => nav("/app/matters")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!m) return null;
  const rename = async (e) => {
    e.preventDefault();
    const name = draft;
    setRenameOpen(false);
    if (!name) return;
    await webApi.patch(`/matters/${id}`, { name });
    load();
  };
  const del = async () => {
    const { data } = await webApi.delete(`/matters/${id}`);
    toast.success(`Deleted. ${data.erased} erased, ${data.kept} kept.`);
    nav("/app/matters");
  };
  const unlink = async (o) => { await webApi.delete(`/matters/${id}/links/${o}`); load(); };
  return (
    <div data-testid="web-matter-detail">
      <PageHead title={m.name}>
        <button onClick={() => { setDraft(m.name); setRenameOpen(true); }} className={ghost} data-testid="matter-rename-button"><Pencil className="h-4 w-4" /> Rename</button>
        <button onClick={() => setLinking(!linking)} className={ghost} data-testid="matter-link-button"><Link2 className="h-4 w-4" /> Link</button>
        <button onClick={() => setDelOpen(true)} className={`${ghost} text-red-700`} data-testid="matter-delete-button"><Trash2 className="h-4 w-4" /> Delete</button>
      </PageHead>
      {m.linked.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="matter-linked-list">
          {m.linked.map((l) => (
            <Pill key={l.id} className="gap-1 py-1">
              <Link to={`/app/matters/${l.id}`} className="hover:underline">{l.name}</Link> <span className="text-[--w-text-3]">({l.kind})</span>
              <button onClick={() => unlink(l.id)} data-testid={`matter-unlink-${l.id}`} className="ml-1 text-[--w-text-3] hover:text-red-700"><X className="h-3 w-3" /></button>
            </Pill>
          ))}
        </div>
      )}
      {linking && <LinkMatter id={id} onDone={() => { setLinking(false); load(); }} />}
      <div className="mt-4">{m.files.length ? <FileTable rows={m.files} onChange={load} matterId={id} /> : <Empty>No files in this matter yet. Forward one on WhatsApp or upload here.</Empty>}</div>
      <p className="mt-4 text-xs text-[--w-text-3]">{m.files.length} file{m.files.length === 1 ? "" : "s"}</p>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="theme-warm">
          <DialogHeader><DialogTitle>Rename matter</DialogTitle></DialogHeader>
          <form onSubmit={rename} className="space-y-4">
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              className="w-full rounded-md border border-[--w-border] bg-[--w-card] px-3 py-2 text-sm text-[--w-text] outline-none focus:border-[--w-accent]" />
            <DialogFooter>
              <button type="button" onClick={() => setRenameOpen(false)} className={ghost}>Cancel</button>
              <button type="submit" className={primary}>Rename</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="theme-warm" onOpenAutoFocus={(e) => { e.preventDefault(); confirmRef.current?.focus(); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete matter</AlertDialogTitle>
            <AlertDialogDescription>{`Delete "${m.name}"? ${m.delete_plan.erase} file(s) will be erased; ${m.delete_plan.stay} stay because they're also in another matter or the Library.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction ref={confirmRef} onClick={del} className="bg-[--w-danger] text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
